import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { namespace } from '@common/config/constants';
import {
    ArrowRight,
    Copy,
    Trash2,
    FileSpreadsheet,
    Pencil,
    Palette,
    Plus,
    ChevronsUpDown,
    CheckSquare,
    ListTree,
    FolderOutput,
    MoreHorizontal
} from 'lucide-react';

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSeparator,
    DropdownMenuSub,
    DropdownMenuSubTrigger,
    DropdownMenuSubContent,
} from "@/components/ui/dropdown-menu";
import { cn } from '@/lib/utils';
import { usePortalContainer } from '@/common/hooks/use-portal-container';

import { TasksGroup } from '@common-models/task-group';
import { Task } from '@common-models/task';
import { selectUser } from '@common-reducers/UserSelectors';
import { getSelectedBoardId } from '@common-reducers/BoardsSelectors';
import {
    taskgroupsSelectors,
    DBTaskgroupsThunks,
    tasksSelectors
} from "@common-reducers/DBServiceThunks";

import { mainSliceActions } from '@common-reducers/MainReducer';
import { modalActions, MODAL } from '@common-reducers/ModalReducer';
import { pickerDialogActions } from '@common-reducers/PickerDialogReducer';
import { updateAllGroupsCollapsedState } from '@common-reducers/TaskThunks'; // setSubtaskStateForTaskList
import { setSubtaskStateForTaskList } from '@common-reducers/TaskThunks';
import { moveGroupToTopOfTheGroupAndReOrderRestOfGroup } from '@common-reducers/TaskThunks';
import { taskActions } from '@common-reducers/TasksReducer';
import { bulkActions } from '@common-reducers/BulkActionsReducer';
import { doActionThunk } from '@common-reducers/UndoRedoThunks';
import { UndoRedoAction } from '@common-reducers/UndoRedoReducer';
import { useDuplicateGroupMutation } from '@common-reducers/backend-api/backend-api';
import { areAllGroupsCollapsedSelector } from '@common-reducers/TaskSelectors';

import PromptDeleteAction from '@common-components/prompt-delete-action/prompt-delete-action';
import MoveToDialog from '@common-components/tasks-panel/bulk-actions-dialog/move-to/move-to-dialog';

interface GroupMenuProps {
    groupId: string;
}

export default function GroupMenu({ groupId }: GroupMenuProps) {
    const { t } = useTranslation(namespace);
    const dispatch = useAppDispatch();
    const localPortalContainer = usePortalContainer();
    const [portalContainer, setPortalContainer] = useState<HTMLElement | ShadowRoot | null>(null);

    const [deletePromptOpen, setDeletePromptOpen] = useState(false);
    const [duplicateCurrentGroup] = useDuplicateGroupMutation();

    const user = useAppSelector(selectUser);
    const selectedBoardId = useAppSelector(getSelectedBoardId);
    const areAllGroupsCollapsed = useAppSelector(areAllGroupsCollapsedSelector);
    
    // Select group data
    const tasksGroup: TasksGroup = useAppSelector((state) =>
        taskgroupsSelectors.selectById(state, groupId)
    );

    const tasks: Task[] = useAppSelector(tasksSelectors.selectAll);
    
    // Bulk Actions state for Move To
    const move_to = useAppSelector((state) => state.BulkActionsReducer.move_to);
    // Be careful with global "menuActiveGroupId" usage in reducers if we switch to local. 
    // However, some thunks might rely on it. We should probably dispatch setMenuActiveGroupId on open if needed,
    // or ensure thunks take groupId as argument. 
    // Most handlers defined here use the closure `groupId/tasksGroup`, effectively localizing logic.

    useEffect(() => {
        const globalPortal = document.getElementById('labels-modals')?.shadowRoot?.querySelector('.btcss-portal-container');
        if (globalPortal) {
            setPortalContainer(globalPortal as HTMLElement);
        }
    }, []);

    if (!tasksGroup) return null;

    const groupItems = tasks.filter((task) => task?.groupId === groupId);

    // --- Handlers ---

    const handleCollapse = () => {
        const clonedTasksGroup = { ...tasksGroup };
        clonedTasksGroup.isCollapsed = !clonedTasksGroup.isCollapsed;
        dispatch(DBTaskgroupsThunks.patch({ entity: clonedTasksGroup }));
    };

    const handleCollapseAll = (bool: boolean) => {
        dispatch(updateAllGroupsCollapsedState(bool));
    };

    const selectAllItems = () => {
        dispatch(bulkActions.setBulkTasksMap(groupItems.reduce<Record<string, Task>>((bulkTaskMap, task) => ({
            ...bulkTaskMap,
            [task._id ?? ""]: task
        }), {})));
    };

    const expandAllSubItems = (expand: boolean) => {
        dispatch(setSubtaskStateForTaskList({
            taskIdList: tasks.map((task) => task._id ?? "").filter((id): id is string => !!id),
            expand
        }));
    };

    const addGroupBelow = () => {
        const clonedTaskGroup = { ...tasksGroup };
        if (user?._id && selectedBoardId) {
            const taskGroup: TasksGroup = {
                name: t('groupMenu.newGroupName'),
                owner: user._id,
                createdBy: user._id,
                boardId: selectedBoardId,
            };
            dispatch(DBTaskgroupsThunks.patch({ entity: { ...tasksGroup, order: (tasksGroup.order ?? 0) + 1 } }));
            dispatch(DBTaskgroupsThunks.create({ ...taskGroup, order: clonedTaskGroup.order }));
        }
    };

    const duplicateGroup = async () => {
        // useDuplicateGroupMutation might depend on argument
        await duplicateCurrentGroup({ groupId });
    };

    const forceGroupEditMode = () => {
        dispatch(taskActions.setForceEditModeToGroupNameByGroupId(tasksGroup._id ?? ""));
    };

    const openColorPicker = () => {
        dispatch(pickerDialogActions.setActiveGroupId(tasksGroup._id ?? ""));
        dispatch(mainSliceActions.toggleGroupMenu('')); // Close menu? 
        // With DropdownMenu it closes automatically on item click, but ColorPicker might need global state helper.
        // Assuming ColorPicker listens to activeGroupId.
    };

    const exportGroup = () => {
        dispatch(modalActions.setComponentToRender(MODAL.EXPORT_TO_EXCEL));
        dispatch(bulkActions.setBulkTasksMap(groupItems.reduce<Record<string, Task>>((bulkTaskMap, task) => ({
            ...bulkTaskMap,
            [task._id ?? ""]: task
        }), {})));
    };

    const handleDeleteClick = () => {
        setDeletePromptOpen(true);
    };

    const handleDeleteConfirmationClosed = (shouldDelete?: boolean) => {
        if (shouldDelete === true) {
            const action: UndoRedoAction<string[], string> = {
                type: 'delete-group-list',
                data: [groupId],
                deletedOrArchived: "deleted"
            };
            dispatch(doActionThunk({ action }));
        }
        setDeletePromptOpen(false);
    };

    // Move To Handlers
    const handleMoveToBoardStart = (e: React.MouseEvent) => {
        e.preventDefault(); // Keep menu open
        dispatch(bulkActions.setBulActionsMoveToId(groupId)); // Ensure ID is set for the global reducer
        // Without this MoveToDialog renders the TASK mover, which only lists the target
        // board's groups and never patches the group
        dispatch(bulkActions.setGroupIsMoving(true));
        dispatch(bulkActions.setMovingTo('board'));
    };

    const handleMoveToGroupStart = (e: React.MouseEvent) => {
        e.preventDefault(); 
        dispatch(bulkActions.setBulActionsMoveToId(groupId));
        dispatch(bulkActions.setGroupIsMoving(false));
        dispatch(bulkActions.setMovingTo('group'));
    };

    const moveGroupToTop = () => {
        // This thunk likely relies on "menuActiveGroupId" in state or we need to patch it?
        // Checking imports: moveGroupToTopOfTheGroupAndReOrderRestOfGroup takes no args?
        // If it relies on mainSlice.menuActiveGroupId, we must dispatch it BEFORE calling.
        // BUT, since we are moving away from global menuActiveGroupId, we might need to verify the thunk.
        // For now, let's try to set it just in case.
        dispatch(mainSliceActions.toggleGroupMenu(groupId));
        dispatch(moveGroupToTopOfTheGroupAndReOrderRestOfGroup());
    };

    // We must ensure 'menuActiveGroupId' is set when menu opens, because some legacy components/thunks
    // (like MoveToDialog or moveGroupToTop) might still rely on it from the Redux store.
    const onOpenChange = (open: boolean) => {
        if (open) {
            dispatch(mainSliceActions.toggleGroupMenu(groupId));
        } else {
            // Optional: clear it, but maybe safer to leave it or clear on full unmount
            // dispatch(mainSliceActions.toggleGroupMenu(''));
            // Reset move_to state on close
            if (move_to) dispatch(bulkActions.setMovingTo(''));
            dispatch(bulkActions.setGroupIsMoving(false));
        }
    };

    return (
        <>
            <PromptDeleteAction 
                title={`${t('groupMenu.deleteGroupPromptTitle')} ${tasksGroup.name}?`} 
                handleClose={handleDeleteConfirmationClosed} 
                open={deletePromptOpen} 
            />

            <DropdownMenu onOpenChange={onOpenChange}>
                <DropdownMenuTrigger asChild>
                    <button className="p-1 rounded-sm text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors bg-transparent border-none cursor-pointer outline-none">
                        <MoreHorizontal size={20} />
                    </button>
                </DropdownMenuTrigger>
                
                <DropdownMenuContent 
                    align="start" 
                    container={portalContainer || localPortalContainer}
                    className="w-64 z-[1400]" 
                >
                    {/* Access / View Options */}
                    <DropdownMenuItem onClick={handleCollapse}>
                         <ChevronsUpDown className="mr-2 h-4 w-4" />
                         <span>{!tasksGroup.isCollapsed ? t('groupMenu.collapseThisGroup') : t('groupMenu.uncollapseThisGroup')}</span>
                    </DropdownMenuItem>

                    <DropdownMenuItem onClick={() => handleCollapseAll(!areAllGroupsCollapsed)}>
                        <ChevronsUpDown className="mr-2 h-4 w-4" />
                        <span>{!areAllGroupsCollapsed ? t('groupMenu.collapseAllGroups') : t('groupMenu.uncollapseAllGroups')}</span>
                    </DropdownMenuItem>

                    <DropdownMenuItem onClick={selectAllItems}>
                        <CheckSquare className="mr-2 h-4 w-4" />
                        <span>{t('groupMenu.selectAllItems')}</span>
                    </DropdownMenuItem>
                    
                    <DropdownMenuSeparator className="bg-gray-200" />

                    <DropdownMenuItem onClick={() => expandAllSubItems(true)}>
                        <ListTree className="mr-2 h-4 w-4" />
                        <span>{t('groupMenu.expandAllSubitems')}</span>
                    </DropdownMenuItem>

                    <DropdownMenuItem onClick={() => expandAllSubItems(false)}>
                        <ListTree className="mr-2 h-4 w-4" />
                        <span>{t('groupMenu.collapseAllSubitems')}</span>
                    </DropdownMenuItem>
                    
                    <DropdownMenuSeparator className="bg-gray-200" />

                    {/* Actions */}
                    <DropdownMenuItem onClick={addGroupBelow}>
                        <Plus className="mr-2 h-4 w-4" />
                        <span>{t('groupMenu.addGroup')}</span>
                    </DropdownMenuItem>

                    <DropdownMenuItem onClick={duplicateGroup}>
                        <Copy className="mr-2 h-4 w-4" />
                        <span>{t('groupMenu.duplicateGroup')}</span>
                    </DropdownMenuItem>

                    {/* Move To Submenu */}
                    <DropdownMenuSub>
                        <DropdownMenuSubTrigger>
                            <FolderOutput className="mr-2 h-4 w-4" />
                            <span>{t('groupMenu.moveTo')}</span>
                        </DropdownMenuSubTrigger>
                        <DropdownMenuSubContent container={portalContainer || localPortalContainer} className="w-56 z-[1405]">
                           {/* If move_to is selected, render the Dialog/UI. This is tricky inside a menu. 
                               We will simply trigger the mode switch. 
                               Actually, MoveToDialog might need to be rendered OUTSIDE if it's large.
                               However, let's try rendering local actions first. 
                           */}
                           
                           {!move_to && (
                               <>
                                   <DropdownMenuItem onClick={handleMoveToBoardStart}>
                                       <span>{t('groupMenu.moveToBoard')}</span>
                                   </DropdownMenuItem>
                                   <DropdownMenuItem onClick={moveGroupToTop}>
                                       <span>{t('groupMenu.moveToTop')}</span>
                                   </DropdownMenuItem>
                                   {/* "Move to group" option if applicable */}
                                   <DropdownMenuItem onClick={handleMoveToGroupStart}>
                                       <span>{t('moveToMenu.moveToGroup')}</span>
                                   </DropdownMenuItem>
                               </>
                           )}
                           
                           {/* Using MoveToDialog inside the menu content might work if we prevent close */}
                           {move_to && (
                               <div className="p-2" onKeyDown={(e) => e.stopPropagation()}> 
                                    {/* This is a hack to show the "dialog" inside the menu. 
                                        MoveToDialog is complex. Ideally it should be a real Dialog. 
                                        For this refactor, we stick to the provided structure. */}
                                    <MoveToDialog />
                               </div>
                           )}

                        </DropdownMenuSubContent>
                    </DropdownMenuSub>

                    <DropdownMenuSeparator className="bg-gray-200" />

                    <DropdownMenuItem onClick={forceGroupEditMode}>
                        <Pencil className="mr-2 h-4 w-4" />
                        <span>{t('groupMenu.renameGroup')}</span>
                    </DropdownMenuItem>

                    <DropdownMenuItem onClick={openColorPicker}>
                        <div 
                            className="mr-2 h-4 w-4 rounded-full border border-gray-200"
                            style={{ backgroundColor: tasksGroup?.colorAccent ?? '#00c875' }}
                        />
                        <span>{t('groupMenu.changeGroupColor')}</span>
                    </DropdownMenuItem>

                    <DropdownMenuSeparator className="bg-gray-200" />

                    <DropdownMenuItem onClick={exportGroup}>
                        <FileSpreadsheet className="mr-2 h-4 w-4" />
                        <span>{t('groupMenu.exportToExcel')}</span>
                    </DropdownMenuItem>

                    <DropdownMenuItem onClick={handleDeleteClick} className="text-red-500 hover:text-red-600 hover:bg-red-50 focus:text-red-600 focus:bg-red-50">
                        <Trash2 className="mr-2 h-4 w-4" />
                        <span>{t('groupMenu.delete')}</span>
                    </DropdownMenuItem>

                </DropdownMenuContent>
            </DropdownMenu>
        </>
    );
}



























